<script setup lang="ts">
import dayjs from 'dayjs';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { mkConfig, generateCsv, download } from 'export-to-csv';

// Composables
const { t } = useI18n();
const { client } = useApiV2();

// Store
const { selectedClient } = storeToRefs(globalStore());
const { getClient } = clientStore();

// Props
const modal = defineModel<boolean>();

// Data
const isCalculated = ref(false);
const csvConfig = mkConfig({ useKeysAsHeaders: true, filename: 'export' });
const csv = ref();
const data = ref<
  {
    start: string;
    end: string;
    area: string;
    areaId: number;
    data: CoreConsumption | null;
    status: 'loading' | 'success' | 'error' | 'pending';
  }[]
>([]);

// Form
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    startDate: yup
      .date()
      .required()
      .test('startDate', t('global.start_date_must_be_before_end_date'), function (value) {
        const { endDate } = this.parent;
        if (!value || !endDate) return true;
        return value <= endDate;
      })
      .test('dateRange', t('global.date_range_must_not_exceed_two_months'), function (value) {
        const { endDate } = this.parent;
        if (!value || !endDate) return true;
        const diffInMonths = dayjs(endDate).diff(dayjs(value), 'month');
        return diffInMonths <= 2;
      }),
    endDate: yup
      .date()
      .required()
      .test('endDate', t('global.end_date_must_be_after_start_date'), function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return value >= startDate;
      }),
    areas: yup.array().required(),
  }),
});

// Computed
const areas = computed(() => {
  return areaStore()
    .getAreas.filter((area) => area.meters.find((meter) => meter.meter_type.energy_type === 'Electricity'))
    .map((area) => ({
      key: area.id,
      label: area.name,
    }));
});

// Methods
const submit = handleSubmit(async (values) => {
  isCalculated.value = false;

  data.value = [];

  values.areas.map((area) => {
    data.value?.push({
      start: values.startDate,
      end: values.endDate,
      area: areas.value.find((a) => a.key === area)?.label ?? '',
      areaId: areas.value.find((a) => a.key === area)?.key ?? 0,
      data: null,
      status: 'pending',
    });
  });

  // Calculate consumption
  for await (const [index] of data.value.entries()) {
    const item = data.value[index];
    if (!item) continue;

    item.status = 'loading';
    try {
      const reponse = await client.GET('/v2/areas/{area_id}/explore/consumptions', {
        params: {
          query: {
            energy_type: 'Electricity',
            start: dayjs(item.start).startOf('day').format('YYYY-MM-DD HH:mm'),
            end: dayjs(item.end).endOf('day').format('YYYY-MM-DD HH:mm'),
            aggregation_level: 'day',
          },
          path: {
            area_id: item.areaId,
          },
        },
      });
      if (reponse.data) {
        item.data = reponse.data;
        item.status = 'success';
      } else {
        item.status = 'error';
      }
    } catch (error) {
      item.status = 'error';
      throw error;
    }
  }

  // Export to CSV
  const clientId = selectedClient.value;
  if (!clientId) return;
  const clientName = getClient(clientId)?.name.toLowerCase().replace(/\s+/g, '_');
  csvConfig.filename = `nrjx_export_${clientName}_${dayjs(values.startDate).format('YYYY-MM-DD')}_${dayjs(values.endDate).format('YYYY-MM-DD')}`;
  const csvData = data.value
    .map((item) =>
      item.data?.items.map((data) => ({
        area: item.area,
        start: data.start,
        end: data.end,
        consumption: data.consumption.quantity,
        cost: data.consumption.cost,
      })),
    )
    .flatMap((item) => item);
  if (!csvData) return;
  csv.value = generateCsv(csvConfig)(csvData);
  isCalculated.value = true;
});

watch(
  () => modal.value,
  () => {
    isCalculated.value = false;
    csv.value = undefined;
    data.value = [];
  },
);
</script>

<template>
  <ui-modal v-model="modal" width="800" :title="$t('global.export_consumption')">
    <div class="grid grid-cols-2 gap-x-4 gap-y-2">
      <p class="text-sm text-gray-600">{{ $t('global.filters') }}</p>
      <p class="text-sm text-gray-600">{{ $t('global.areas') }}</p>
      <div class="col-span-1 bg-gray-50 border border-gray-200 p-4 rounded-md">
        <ui-form-input-date-new
          name="startDate"
          required
          :rules="[{ type: 'disabled-days-after', value: [dayjs().add(1, 'day').toDate()] }]"
          :label="$t('global.start_date')"
        />
        <ui-form-input-date-new
          name="endDate"
          required
          :rules="[{ type: 'disabled-days-after', value: [dayjs().add(1, 'day').toDate()] }]"
          :label="$t('global.end_date')"
        />
        <ui-form-input-multi-select name="areas" required :label="$t('global.areas')" :items="areas" />

        <ui-button class="w-full mt-8" @click="submit">{{ $t('global.calculate') }}</ui-button>
      </div>

      <div class="col-span-1 flex flex-col gap-y-2">
        <div v-if="data.length === 0" class="flex items-center justify-center pt-8">
          <p class="text-sm text-gray-600">{{ $t('global.no_area_selected') }}</p>
        </div>
        <template v-else>
          <div
            v-for="item in data"
            :key="item.area"
            class="flex items-center justify-between bg-gray-50 border border-gray-200 p-1 px-2 rounded-md"
          >
            <ui-badge color="primary">{{ item.area }}</ui-badge>
            <div class="flex items-center justify-center gap-x-2">
              <ui-loader v-if="item.status === 'loading'" class="!w-3 !stroke-amber-600" />
              <ui-icon v-else-if="item.status === 'success'" name="Check" class="text-green-600 w-3" />
              <ui-icon v-else-if="item.status === 'pending'" name="CircleDashed" class="text-gray-600 w-3" />
              <p v-if="item.status === 'pending'" class="text-xs text-gray-600">{{ $t(`global.recovery_of_pending_data`) }}</p>
              <p v-if="item.status === 'loading'" class="text-xs text-amber-600">{{ $t('global.data_recovery_in_progress') }}</p>
              <p v-if="item.status === 'success'" class="text-xs text-green-600">{{ $t(`global.data_recovered`) }}</p>
              <p v-if="item.status === 'error'" class="text-xs text-red-600">{{ $t(`global.error`) }}</p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <ui-button :color="isCalculated ? 'primary' : 'disabled'" @click="download(csvConfig)(csv)">{{ $t('global.download') }}</ui-button>
    </template>
  </ui-modal>
</template>
